import type { BehaviorGraph } from "../layout/types.js";

/** Approximate behavior node width when laying out graphs without measured DOM sizes. */
const ESTIMATED_BEHAVIOR_NODE_WIDTH = 240;

/** Approximate behavior node height (aligned with default terminal node vertical offset). */
const ESTIMATED_BEHAVIOR_NODE_HEIGHT = 100;

/**
 * Translates all nodes so the graph bounding box is centered in the given viewport.
 *
 * @param graph - Behavior graph to center (nodes are copied, connections unchanged).
 * @param canvasWidth - Visible canvas width in world coordinates (zoom = 1).
 * @param canvasHeight - Visible canvas height in world coordinates (zoom = 1).
 * @returns A new graph with translated node positions.
 */
export function centerBehaviorGraphInViewport(
  graph: BehaviorGraph,
  canvasWidth: number,
  canvasHeight: number,
): BehaviorGraph {
  const { nodes, connections } = graph;
  if (nodes.length === 0) {
    return graph;
  }

  const width = Math.max(1, canvasWidth);
  const height = Math.max(1, canvasHeight);

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const node of nodes) {
    minX = Math.min(minX, node.x);
    minY = Math.min(minY, node.y);
    maxX = Math.max(maxX, node.x + ESTIMATED_BEHAVIOR_NODE_WIDTH);
    maxY = Math.max(maxY, node.y + ESTIMATED_BEHAVIOR_NODE_HEIGHT);
  }

  const graphCenterX = (minX + maxX) / 2;
  const graphCenterY = (minY + maxY) / 2;
  const deltaX = Math.round(width / 2 - graphCenterX);
  const deltaY = Math.round(height / 2 - graphCenterY);

  if (deltaX === 0 && deltaY === 0) {
    return graph;
  }

  return {
    nodes: nodes.map((node) => ({
      ...node,
      x: node.x + deltaX,
      y: node.y + deltaY,
    })),
    connections,
  };
}
